import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, DatePicker, Select, TimePicker, InputNumber } from "antd";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import dayjs from "dayjs";
import { requiredField } from "utils/crudSagaHelper";
import {
  getEmployeeScheduleDetails,
  doEmployeeScheduleCreate,
  doEmployeeScheduleUpdate,
} from "../../../../../redux/employeeschedule/employeeScheduleState";

import { DATE_FORMAT } from "variables/constants";
import { formatDateTimeOrNull } from "utils";
import { MODULE_EMPLOYEES } from "variables/constants";
import { TIME_FORMAT_12HM } from "variables/constants";
import SwitchField from "components/fields/SwitchField";

const EmployeeScheduleForm = ({
  onCancel,
  employeeId,
  submitLabel,
  ...props
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { recordId, mode } = props;

  const recordData = useSelector((state) => state.employeeSchedule.employeeScheduleDetails?.data);
  const scheduleTemplateOptions = recordData?.templateOptions || [];
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",];
  const { RangePicker } = TimePicker;
  const [dateToPickerValue, setDateToPickerValue] = useState(dayjs());

  const doCreate = (data) => dispatch(doEmployeeScheduleCreate(data));
  const doUpdate = (data) => dispatch(doEmployeeScheduleUpdate(data));

  useEffect(() => {
    if (!recordData) {
      return;
    }

    form.resetFields();
    const existingSchedules = recordData.schedules || [];

    const schedules =
      recordData.esd_id > 0 && existingSchedules.length > 0
        ? existingSchedules.map((schedule) => ({
            esd_id: schedule.esd_id,
            esd_day: schedule.esd_day,
            esd_type_id: schedule.esd_type_id ?? null,
            esd_time_in: schedule.esd_time_in ?? null,
            esd_time_out: schedule.esd_time_out ?? null,
            esd_is_flexible: schedule.esd_is_flexible ?? 0,
            esd_flexible_time: schedule.esd_flexible_time ?? null,
            time_range:
              schedule.esd_time_in && schedule.esd_time_out
                ? [
                    dayjs(schedule.esd_time_in, "HH:mm:ss"),
                    dayjs(schedule.esd_time_out, "HH:mm:ss"),
                  ]
                : [],
          }))
        : Array.from({ length: 7 }, (_, index) => ({
            esd_id: null,
            esd_day: index,
            esd_type_id: null,
            esd_time_in: null,
            esd_time_out: null,
            esd_is_flexible: 0,
            esd_flexible_time: null,
            time_range: [],
          }));

    const fromDate = recordData.esd_effective_date_from
      ? dayjs(recordData.esd_effective_date_from)
      : null;

    if (fromDate) {
      setDateToPickerValue(fromDate);
    }
    
    form.setFieldsValue({
      
      esd_employee_id: employeeId,
      esd_id: recordData.esd_id ?? 0,
      esd_effective_date_from: recordData.esd_effective_date_from
        ? dayjs(recordData.esd_effective_date_from)
        : null,
      esd_effective_date_to: recordData.esd_effective_date_to
        ? dayjs(recordData.esd_effective_date_to)
        : null,
      esd_template_id: recordData.esd_template_id ?? null,
      schedules,
    });
  }, [recordData, employeeId, form]);

  const handleTemplateChange = (templateId) => {
    const template = scheduleTemplateOptions.find(
      (option) => option.value === templateId
    );

    if (!template) {
      return;
    }

  const currentSchedules = form.getFieldValue("schedules") || [];
  const schedules = template.schedules.map((schedule, index) => ({
    esd_id: currentSchedules[index]?.esd_id ?? null,
    esd_day: index,
    esd_type_id: schedule.esd_type_id ?? null,
    esd_time_in: schedule.esd_time_in ?? null,
    esd_time_out: schedule.esd_time_out ?? null,
    esd_is_flexible: schedule.esd_is_flexible ?? 0,
    esd_flexible_time: schedule.esd_flexible_time ?? null,
    time_range:
      schedule.esd_time_in && schedule.esd_time_out
        ? [
            dayjs(schedule.esd_time_in, "HH:mm:ss"),
            dayjs(schedule.esd_time_out, "HH:mm:ss"),
          ]
        : [],
  }));

    form.setFieldsValue({
      schedules,
    });
  };

  useEffect(() => {
    dispatch(
      getEmployeeScheduleDetails({
        employeeId,
        scheduleId: recordId,
      })
    );
  }, [dispatch, employeeId, recordId]);

  const onSubmit = (formData) => {
    const schedules = (formData.schedules || []).map((schedule) => ({
      ...schedule,
      esd_time_in: schedule.time_range?.[0]
        ? schedule.time_range[0].format("HH:mm:ss")
        : null,
      esd_time_out: schedule.time_range?.[1]
        ? schedule.time_range[1].format("HH:mm:ss")
        : null,
    }));

    const data = {
      ...formData,
      esd_employee_id: employeeId,
      esd_effective_date_from: formatDateTimeOrNull(
        formData.esd_effective_date_from,
        DATE_FORMAT
      ),
      esd_effective_date_to: formatDateTimeOrNull(
        formData.esd_effective_date_to,
        DATE_FORMAT
      ),
      schedules,
    };

    if (recordData?.esd_id > 0) {
      doUpdate({
        ...data,
        esd_id: recordData.esd_id,
      });
    } else {
      doCreate(data);
    }
  };
  const handleValuesChange = (changedValues) => {
    if (!form.getFieldValue("esd_template_id")) {
      return;
    }

    if (changedValues.schedules) {
      form.setFieldValue("esd_template_id", null);
    }
  };
  return (
    <Form
      className={mode !== "view" ? "form" : ""}
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      onValuesChange={handleValuesChange}
    >
      <FormItem name="esd_employee_id" hidden>
        <Input type="hidden" />
      </FormItem>

      <FormItem name="esd_id" hidden>
        <Input type="hidden" />
      </FormItem>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {mode !== "view" ? (
          <FormItem
            label="Effective Date From"
            name="esd_effective_date_from"
            rules={[requiredField()]}
          >
            <DatePicker
              id="esd_effective_date_from"
              className="input-field"
              format={DATE_FORMAT}
              style={{ width: "100%" }}
              onChange={(date) => {
                if (date) {
                  setDateToPickerValue(date);
                }
              }}
            />
          </FormItem>
        ) : (
          <div className="p-2">
            <h2 className="text-sm font-medium muted-text">
              Effective Date From
            </h2>
            <label className="text-base label-value dark:text-white">
              {recordData?.esd_effective_date_from || "-"}
            </label>
          </div>
        )}

        {mode !== "view" ? (
          <FormItem
            label="Effective Date To"
            name="esd_effective_date_to"
            dependencies={["esd_effective_date_from"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const fromDate = getFieldValue(
                    "esd_effective_date_from"
                  );

                  if (!value || !fromDate) {
                    return Promise.resolve();
                  }

                  if (value.isBefore(fromDate, "day")) {
                    return Promise.reject(
                      new Error(
                        "Effective Date To cannot be earlier than From"
                      )
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker
              id="esd_effective_date_to"
              className="input-field"
              format={DATE_FORMAT}
              style={{ width: "100%" }}
              pickerValue={dateToPickerValue}
              onPanelChange={(value) => {
                setDateToPickerValue(value);
              }}
              disabledDate={(current) => {
                const fromDate = form.getFieldValue(
                  "esd_effective_date_from"
                );

                return (
                  fromDate &&
                  current.isBefore(fromDate, "day")
                );
              }}
            />
          </FormItem>
        ) : (
          <div className="p-2">
            <h2 className="text-sm font-medium muted-text">
              Effective Date To
            </h2>
            <label className="text-base label-value dark:text-white">
              {recordData?.esd_effective_date_to || "-"}
            </label>
          </div>
        )}

        {mode !== "view" ? (
          <FormItem
            label="Schedule Template"
            name="esd_template_id"
          >
            <Select
              id="esd_template_id"
              className="input-field"
              allowClear
              options={scheduleTemplateOptions}
              onChange={handleTemplateChange}
              placeholder="Select Schedule Template"
            />
          </FormItem>
        ) : (
          <div className="p-2">
            <h2 className="text-sm font-medium muted-text">
              Schedule Template
            </h2>
            <label className="text-base label-value dark:text-white">
              {recordData?.schedule_template_name || "-"}
            </label>
          </div>
        )}
      </div>
      <div className="w-full">
        <Form.List 
          name="schedules" 

        >
            {(fields) =>
              fields.map((field, index) => (
                <div
                  className="grid grid-cols-1 md:grid-cols-7 gap-y-2 gap-x-4 mb-2 border-2 rounded-2xl p-2 w-full"
                  key={field.key}
                >
                  <FormItem 
                    {...field}
                    name={[index, "esd_day"]}
                    initialValue={index}
                    hidden
                  >
                    <Input type="hidden" />
                  </FormItem>
                  <FormItem 
                    {...field}
                    name={[index, "esd_id"]}
                    hidden
                  >
                    <Input type="hidden"/>
                  </FormItem>
                  <FormItem extra="md:col-span-2"
                    {...field} 
                    name={[index, "esd_type_id"]}
                    label={daysOfWeek[index] || `Day ${index + 1}`}
                    rules={[requiredField()]}
                  >
                    <Select id="esd_type_id"
                      className="input-field"
                      showSearch
                      allowClear={true}
                      options={recordData?.scheduleTypeOptions}
                    /> 
                  </FormItem>	
                  <FormItem
                    extra="md:col-span-2"
                    {...field}
                    name={[index, "time_range"]}
                    label={<span className="px-2">Time In / Time Out</span>}
                  >
                    <RangePicker
                      picker="time"
                      format={TIME_FORMAT_12HM}
                      className="input-field"
                      style={{ width: "100%" }}
                      allowClear
                    />
                  </FormItem>
                  <div className="flex gap-2 md:col-span-3">
                    <FormItem
                      {...field}
                      valuePropName="checked"
                      name={[index, "esd_is_flexible"]}
                      label={<span className="px-2">Is Flexible</span>}
                    >
                      <SwitchField
                        id={`esd_is_flexible_${index}`}
                        type="text"
                        labelYes="Yes"
                        labelNo="No"
                      />
                    </FormItem>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.schedules?.[index]?.esd_is_flexible !==
                        currentValues.schedules?.[index]?.esd_is_flexible
                      }
                    >
                      {({ getFieldValue }) => {
                        const isFlexible = getFieldValue([
                          "schedules",
                          index,
                          "esd_is_flexible",
                        ]);

                        return isFlexible ? (
                          <FormItem
                            {...field}
                            name={[index, "esd_flexible_time"]}
                            label="Enter Flexible Time"
                            rules={[requiredField()]}
                          >
                            <InputNumber
                              className="input-field"
                              placeholder="Flexible time"
                              min={1}
                            />
                          </FormItem>
                        ) : null;
                      }}
                    </Form.Item>
                  </div>
                </div>
              ))
            }
        </Form.List>
      </div>
      <div className="w-full flex justify-end">
        {mode === "edit" && (
          <CrudButtons
            mode={mode}
            recordId={recordData?.esd_id ?? 0}
            isModal={true}
            modalId={
              recordData?.esd_id > 0
                ? "edit_employee_schedule"
                : "add_employee_schedule"
            }
            cancelLabel="Cancel"
            doHideModal={onCancel}
            submitLabel={submitLabel}
            moduleId={MODULE_EMPLOYEES}
          />
        )}
      </div>
    </Form>
  );
};

export default EmployeeScheduleForm;